import { useInView, useMotionValue, useSpring } from "motion/react";
import { useEffect, useRef } from "react";

interface AnimateNumberProps {
  value: number;
}

export const AnimateNumber: React.FC<AnimateNumberProps> = ({
  value,
}: AnimateNumberProps) => {
  const ref = useRef<HTMLSpanElement>(null);

  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, {
    duration: 2000,
    bounce: 0,
    stiffness: 200, // 高刚度，快速接近目标值
    damping: 40, // 高阻尼，减少回弹
    mass: 1, // 默认质量
    restDelta: 0.3, // 当距离目标值小于 0.1 时停止
    restSpeed: 0.1, // 当速度低于 0.1/秒 时停止
  });
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      // 如果 value 为 0，直接设置并显示
      if (value === 0) {
        motionValue.set(0);
        if (ref.current) {
          ref.current.textContent = value.toString();
        }
        return;
      }
      motionValue.set(value);
    }
  }, [isInView, value, motionValue]);

  useEffect(() => {
    // 如果 value 为 0，不需要监听动画变化
    if (value === 0) {
      return;
    }

    const unsubscribe = springValue.on("change", (latest) => {
      if (ref.current) {
        // 分离整数部分和小数部分
        const [, decimalPart] = value.toString().split(".");
        const animatedInteger = Math.floor(latest);

        // 只有整数部分跳动，小数部分保持固定
        if (decimalPart) {
          ref.current.textContent = `${animatedInteger}.${decimalPart}`;
        } else {
          ref.current.textContent = animatedInteger.toString();
        }

        // 检查当前值是否小于等于目标值，避免超出
        if (animatedInteger >= Math.floor(value)) {
          ref.current.textContent = value.toString();
        }
      }
    });
    return () => unsubscribe();
  }, [springValue, value]);

  return <span ref={ref}></span>;
};
